import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  UseFieldArrayInsert,
  UseFieldArrayRemove,
  useFormContext,
  useWatch,
} from "react-hook-form";

import { errorHandler } from "#app/monitoring/errorHandler";
import { showErrorToast } from "#app/notifications";
import { deleteProductLines } from "#modules/productLines/infra/deleteProductLines.api";
import { quoteRequestLineQuery } from "#modules/quoteRequestLines/infra/quoteRequestLine.query";
import { QuoteRequestLineFormData } from "#modules/quoteRequestLines/types/QuoteRequestLineFormData";

type DeleteProductLineContext = {
  productLineToDelete?: {
    id?: string;
    key?: string;
    product?: {
      id?: string;
      name: string;
      price: number;
    };
    quantity: number;
  };
};

export const useDeleteProductLine = ({
  index,
  removeProduct,
  insertProduct,
  options,
}: {
  index: number;
  removeProduct: UseFieldArrayRemove;
  insertProduct: UseFieldArrayInsert<QuoteRequestLineFormData, "productLines">;
  options?: {
    onMutate?: () => DeleteProductLineContext;
    onSuccess?: () => void;
    onError?: (err: Error, context: unknown) => void;
  };
}) => {
  const queryClient = useQueryClient();

  const { control } = useFormContext<QuoteRequestLineFormData>();

  const productLines = useWatch({ control, name: "productLines" });
  const quoteRequestLineId = useWatch({ control, name: "quoteRequestLineId" });

  const { mutate, ...rest } = useMutation({
    mutationFn: deleteProductLines,

    onMutate: () => {
      const productLineToDelete = productLines?.[index];
      removeProduct(index);

      return { productLineToDelete };
    },

    onSuccess: async () => {
      void queryClient.invalidateQueries(quoteRequestLineQuery({ id: quoteRequestLineId ?? "" }));

      options?.onSuccess?.();
    },

    onError: (err: Error, _newProductLine, context) => {
      showErrorToast({ title: err.message });
      errorHandler(err);

      if (context?.productLineToDelete) {
        insertProduct(index, context.productLineToDelete);
      }

      options?.onError?.(err, context);
    },
  });

  return { deleteProductLine: mutate, ...rest };
};
